// Relances sur devis (Messagerie) — reprise du classeur « RELANCES CLIENTS ».
//
// Table SÉPARÉE du module Devis : le classeur porte plusieurs années saisies à
// la main, avec ses trous, et les injecter dans les devis du CRM abîmerait des
// données propres. On les garde côte à côte, et l'écran rapproche les deux par
// numéro de pièce.

// Ré-export : la page consomme la composition du SMS via ce module. Elle vit
// dans le paquet partagé, où son coût est mis à l'épreuve par des tests.
export { estGsm7, messageRelanceDevis, segmentsSms } from '@imp/shared/sms';

import {
  analyserRelancesDevis,
  cleNumeroDevis,
  type DevisRelance,
  type StatutDevis,
} from '@imp/shared/relances-devis';

import { lireTout } from '@/lib/pagination';
import { supabase } from '@/lib/supabase';
import { ORG_ID } from '@/modules/clients/api';

export type { StatutDevis };

export const STATUTS_DEVIS: { id: StatutDevis; label: string; teinte: string }[] = [
  // Le vert et le rouge disent ici l'issue commerciale, comme ailleurs le sens
  // de l'argent : accepté = gagné, refusé = perdu.
  { id: 'sans_reponse', label: 'Sans réponse', teinte: 'bg-amber-100 text-amber-800' },
  { id: 'en_attente', label: 'En attente', teinte: 'bg-sky-100 text-sky-800' },
  { id: 'accepte', label: 'Accepté', teinte: 'bg-emerald-100 text-emerald-800' },
  { id: 'refuse', label: 'Refusé', teinte: 'bg-red-100 text-red-700' },
];

export interface DevisExterne extends DevisRelance {
  id: string;
  /** Le CRM connaît-il ce devis sous son propre numéro ? */
  dansLeCrm: boolean;
}

/** Devis du classeur, enrichis du rapprochement avec les devis du CRM. */
export async function listDevisExternes(): Promise<DevisExterne[]> {
  const [lignes, devisCrm] = await Promise.all([
    lireTout<{
      id: string;
      numero: string;
      client: string | null;
      ville: string | null;
      telephone: string | null;
      objet: string | null;
      date_envoi: string | null;
      montant: number | string;
      statut: StatutDevis;
      commentaire: string | null;
      annee: number;
    }>((de, a) =>
      supabase
        .from('devis_externes')
        .select(
          'id,numero,client,ville,telephone,objet,date_envoi,montant,statut,commentaire,annee',
        )
        .eq('organization_id', ORG_ID)
        .is('deleted_at', null)
        .order('date_envoi', { ascending: true, nullsFirst: false })
        .order('numero')
        .range(de, a),
    ),
    lireTout<{ numero: string }>((de, a) =>
      supabase.from('devis').select('numero').is('deleted_at', null).order('numero').range(de, a),
    ),
  ]);

  const clesCrm = new Set(devisCrm.map((d) => cleNumeroDevis(d.numero)));
  return lignes.map((l) => ({
    ...l,
    montant: Number(l.montant),
    dansLeCrm: clesCrm.has(cleNumeroDevis(l.numero)),
  }));
}

export interface ResultatImportRelances {
  enregistres: number;
  datesIgnorees: number;
  doublons: number;
}

/** Lit un classeur en conservant le NOM de chaque feuille (l'année y figure). */
export async function lireFeuillesNommees(
  file: File,
): Promise<{ nom: string; lignes: unknown[][] }[]> {
  const XLSX = await import('xlsx');
  // Sans `cellDates` : les dates restent des nombres Excel, convertis en UTC
  // par l'analyseur partagé. Les objets Date dépendent du fuseau du navigateur.
  const wb = XLSX.read(await file.arrayBuffer(), {});
  return wb.SheetNames.map((nom) => {
    const f = wb.Sheets[nom];
    return {
      nom,
      lignes: f ? XLSX.utils.sheet_to_json<unknown[]>(f, { header: 1, defval: null }) : [],
    };
  });
}

/**
 * Importe le classeur de relances.
 *
 * Upsert sur le numéro de devis : réimporter le même fichier ne crée pas de
 * doublon et met à jour ce qui a changé. Le statut et le commentaire modifiés
 * dans le CRM sont donc écrasés par le classeur — c'est voulu tant que le
 * classeur reste la source, et l'écran le dit.
 */
export async function importerRelancesDevis(file: File): Promise<ResultatImportRelances> {
  const { devis, datesIgnorees, doublons } = analyserRelancesDevis(await lireFeuillesNommees(file));
  if (devis.length === 0) throw new Error('aucun devis reconnu dans ce fichier');

  for (let i = 0; i < devis.length; i += 500) {
    const lot = devis.slice(i, i + 500).map((d) => ({ organization_id: ORG_ID, ...d }));
    const { error } = await supabase
      .from('devis_externes')
      .upsert(lot, { onConflict: 'organization_id,numero' });
    if (error) throw new Error(error.message);
  }
  return { enregistres: devis.length, datesIgnorees, doublons };
}

export async function updateDevisExterne(
  id: string,
  patch: { statut?: StatutDevis; commentaire?: string },
): Promise<void> {
  const { error } = await supabase.from('devis_externes').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}
